declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag: (...args: unknown[]) => void;
	}
}

export const gtag = (...args: unknown[]): void => {
	if (typeof window !== 'undefined') {
		if (typeof window.gtag === 'undefined') {
			window.dataLayer = window.dataLayer ?? [];
			// oxlint-disable-next-line consistent-function-scoping
			window.gtag = function gtag() {
				window.dataLayer?.push(arguments);
			};
		}

		window.gtag(...args);
	}
};

/**
 * Query-string parameters that must never be reported to analytics because they
 * carry PII or credentials: a donor's email address, a password-reset session
 * key, or a redirect target that itself embeds those values.
 */
const SENSITIVE_ANALYTICS_PARAMS = new Set(['email', 'redirect', 'session', 'token', 'access_token', 'password']);

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;

/**
 * Redacts PII and credentials from a URL before it is sent to Google Analytics
 * as `page_location`.
 *
 * Donor emails and password-reset keys travel through the SPA query string
 * (e.g. `/confirm/code?email=donor@example.com`), so reporting the raw
 * `location.href` would leak them to a third party. This keeps the path and the
 * benign analytics params intact while redacting any sensitive value — both by
 * a known-sensitive parameter name and by an email-shaped catch-all that also
 * covers values nested inside a `redirect` param.
 */
export const sanitizeAnalyticsUrl = (href: string): string => {
	try {
		const url = new URL(href);
		const keysToRedact: string[] = [];
		url.searchParams.forEach((value, key) => {
			if (SENSITIVE_ANALYTICS_PARAMS.has(key.toLowerCase()) || EMAIL_PATTERN.test(value)) {
				keysToRedact.push(key);
			}
		});
		for (const key of keysToRedact) {
			url.searchParams.set(key, 'redacted');
		}
		return url.toString();
	} catch {
		// A malformed URL should never take analytics (or the app) down; drop the
		// query string entirely so nothing sensitive can slip through.
		return href.split('?')[0] ?? href;
	}
};
